import { Plus, X } from "lucide-react";
import { MAX_HOSTS_PER_SITE } from "@headerhawk/core/hosts";
import { Button, Input } from "@headerhawk/ui";
import {
  rowsToPatterns,
  subdomainOptionFor,
  type HostRow,
} from "@/lib/validation/site";

interface HostListFieldProps {
  /** Prefix for element ids, so two editors on a page do not collide. */
  idPrefix: string;
  rows: HostRow[];
  onChange: (rows: HostRow[]) => void;
  onBlur?: () => void;
  /** Error about the list as a whole. */
  listError?: string | undefined;
  /** Errors about single rows, by row index. */
  rowErrors?: Record<number, string> | undefined;
  /** Offer "Add another host". Onboarding asks for one host and a checkbox. */
  allowMultiple?: boolean;
  disabled?: boolean;
  inputRef?: React.Ref<HTMLInputElement>;
}

/**
 * Edits the hosts a site accepts reports from (task 303).
 *
 * Each row is a host and a checkbox that also accepts every subdomain of that
 * host, as typed: `*.dev.example.com` beside `dev.example.com`, never
 * `*.example.com` (task 418). The checkbox is how a wildcard is added in the
 * common case - `*.example.com` never matches `example.com` itself, so
 * "example.com and all its subdomains" is two patterns, and one click.
 */
export function HostListField({
  idPrefix,
  rows,
  onChange,
  onBlur,
  listError,
  rowErrors,
  allowMultiple = false,
  disabled = false,
  inputRef,
}: HostListFieldProps) {
  const update = (index: number, change: Partial<HostRow>) =>
    onChange(rows.map((row, i) => (i === index ? { ...row, ...change } : row)));

  const canAdd =
    allowMultiple && rowsToPatterns(rows).patterns.length < MAX_HOSTS_PER_SITE;
  const listErrorId = `${idPrefix}-hosts-error`;
  const hintId = `${idPrefix}-hosts-hint`;

  return (
    <fieldset className="space-y-3">
      <legend className="text-sm font-medium leading-none">
        {allowMultiple ? "Hosts" : "Host"}
      </legend>
      <p id={hintId} className="text-sm text-muted-foreground">
        {allowMultiple
          ? "Reports are kept only from pages on these hosts. A wildcard such as *.example.com matches every subdomain, but not example.com itself."
          : "The host where you'll add CSP headers (e.g., example.com). Reports from other hosts are dropped."}
      </p>

      {rows.map((row, index) => {
        const inputId = `${idPrefix}-host-${index}`;
        const errorId = `${inputId}-error`;
        const rowError = rowErrors?.[index];
        const option = subdomainOptionFor(row.host);
        const label = allowMultiple ? `Host ${index + 1}` : "Host";

        return (
          <div key={index} className="space-y-2" data-testid="host-row">
            <div className="flex items-center gap-2">
              <Input
                ref={index === 0 ? inputRef : undefined}
                id={inputId}
                type="text"
                aria-label={label}
                value={row.host}
                onChange={(e) => update(index, { host: e.target.value })}
                onBlur={onBlur}
                placeholder="example.com"
                maxLength={255}
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                disabled={disabled}
                aria-invalid={rowError || listError ? true : undefined}
                aria-describedby={
                  rowError ? errorId : listError ? listErrorId : hintId
                }
              />
              {allowMultiple && rows.length > 1 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => onChange(rows.filter((_, i) => i !== index))}
                  disabled={disabled}
                  aria-label={`Remove ${row.host || label}`}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
            {rowError && (
              <p id={errorId} className="text-sm text-destructive">
                {rowError}
              </p>
            )}
            {option && (
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-input accent-primary"
                  checked={row.includeSubdomains}
                  onChange={(e) =>
                    update(index, { includeSubdomains: e.target.checked })
                  }
                  disabled={disabled}
                />
                <span>
                  Also accept all subdomains of{" "}
                  <span className="font-mono">{option.host}</span>
                </span>
              </label>
            )}
          </div>
        );
      })}

      {listError && (
        <p id={listErrorId} className="text-sm text-destructive">
          {listError}
        </p>
      )}

      {canAdd && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() =>
            onChange([...rows, { host: "", includeSubdomains: false }])
          }
          disabled={disabled}
        >
          <Plus className="mr-1 h-4 w-4" />
          Add another host
        </Button>
      )}
    </fieldset>
  );
}
